// =============================================================================
// Environment: how staging / production work
// =============================================================================
//
// 1) Local dev (`npm start`)
//    - API defaults to same-origin `/api/v2` (see setupProxy.js → your backend).
//    - Do not point REACT_APP_API_BASE at http://localhost:4000/... (CORS + vendor headers).
//
// 2) Staging or production builds (`npm run build`)
//    - This repo does not require a `.env` file. If you have none, set **`BUILD_DEFAULT_TARGET`**
//      below (`"staging"` | `"production"` | `"local"`).
//    - Optional: add `.env.local` with `REACT_APP_*` variables, or set the same variables in CI.
//      Example:
//        REACT_APP_BASE_URL=https://testingapi.bharatselfdrive.com
//        REACT_APP_API_BASE=https://testingapi.bharatselfdrive.com/api/v2
//        REACT_APP_AUTH_API=https://testingapi.bharatselfdrive.com/api/v2
//      (Use https://api.bharatselfdrive.com for production.)
//
// 3) Optional override without editing this file:
//        REACT_APP_DEPLOY_TARGET=staging   | production | local
//
// =============================================================================

/** Used only when `NODE_ENV !== "development"` and REACT_APP_* URLs are not set. */
const BUILD_DEFAULT_TARGET = "staging"; // "staging" | "production" | "local"

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");

const envApiBase =
  typeof process !== "undefined" &&
  process.env &&
  typeof process.env.REACT_APP_API_BASE === "string" &&
  process.env.REACT_APP_API_BASE.trim()
    ? trimTrailingSlash(process.env.REACT_APP_API_BASE)
    : "";

const envAuthApi =
  typeof process !== "undefined" &&
  process.env &&
  typeof process.env.REACT_APP_AUTH_API === "string" &&
  process.env.REACT_APP_AUTH_API.trim()
    ? trimTrailingSlash(process.env.REACT_APP_AUTH_API)
    : "";

const envBaseUrl =
  typeof process !== "undefined" &&
  process.env &&
  typeof process.env.REACT_APP_BASE_URL === "string" &&
  process.env.REACT_APP_BASE_URL.trim()
    ? trimTrailingSlash(process.env.REACT_APP_BASE_URL)
    : "";

const isDev =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "development";

const envDeployTarget =
  typeof process !== "undefined" &&
  process.env &&
  typeof process.env.REACT_APP_DEPLOY_TARGET === "string" &&
  process.env.REACT_APP_DEPLOY_TARGET.trim()
    ? process.env.REACT_APP_DEPLOY_TARGET.trim().toLowerCase()
    : "";

/** Dev requests to localhost:4000 from :3000 fail CORS for vendor headers unless proxied. */
const isLocalBackendUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const u = url.toLowerCase();
  return u.includes("localhost:4000") || u.includes("127.0.0.1:4000");
};

const BUILT_IN = {
  staging: {
    BaseUrl: "https://testingapi.bharatselfdrive.com",
    apiEndpoint: "https://testingapi.bharatselfdrive.com/api/v2",
    authApiEndpoint: "https://testingapi.bharatselfdrive.com/api/v2",
  },
  production: {
    BaseUrl: "https://api.bharatselfdrive.com",
    apiEndpoint: "https://api.bharatselfdrive.com/api/v2",
    authApiEndpoint: "https://api.bharatselfdrive.com/api/v2",
  },
  local: {
    BaseUrl: "http://localhost:4000",
    apiEndpoint: "http://localhost:4000/api/v2",
    authApiEndpoint: "http://localhost:4000/api/v2",
  },
};

const resolvedBuildTarget = () => {
  const fromEnv = envDeployTarget;
  if (fromEnv && BUILT_IN[fromEnv]) return fromEnv;
  const d = String(BUILD_DEFAULT_TARGET || "staging").toLowerCase();
  return BUILT_IN[d] ? d : "staging";
};

const builtInForBuild = () => BUILT_IN[resolvedBuildTarget()] || BUILT_IN.staging;

/**
 * In development, API calls should be same-origin `/api/v2` (setupProxy) unless you
 * intentionally set REACT_APP_API_BASE to a remote host (e.g. staging).
 */
const resolveApiEndpoint = () => {
  if (!isDev) {
    return envApiBase || builtInForBuild().apiEndpoint;
  }
  if (envApiBase) {
    if (envApiBase.startsWith("/")) return envApiBase;
    if (isLocalBackendUrl(envApiBase)) return "/api/v2";
    return envApiBase;
  }
  return "/api/v2";
};

const resolveAuthApiEndpoint = () => {
  if (!isDev) {
    return envAuthApi || builtInForBuild().authApiEndpoint;
  }
  if (envAuthApi) {
    if (envAuthApi.startsWith("/")) return envAuthApi;
    if (isLocalBackendUrl(envAuthApi)) return "/api/v2";
    return envAuthApi;
  }
  return "/api/v2";
};

export const BaseUrl = envBaseUrl || (isDev ? "http://localhost:4000" : builtInForBuild().BaseUrl);
export const apiEndpoint = resolveApiEndpoint();
export const authApiEndpoint = resolveAuthApiEndpoint();

export const GOOGLE_PLACES_API_KEY =
  "AIzaSyBoCgpI2He8LNoG8hL1PHLUsUCK96AhKqQ";
export const GOOGLE_MAPS_API_KEY =
  "AIzaSyA9f1886nyARbq6RRT47aFIXAIIINtWmXI";
// Backward compatibility for existing code paths.
export const GOOGLE_MAP_KEY =
  "AIzaSyA9f1886nyARbq6RRT47aFIXAIIINtWmXI";
export const MAP_AUTO_REFESH = 45;
export const referralEndpoint =
  "https://referral.bharatselfdrive.com/api/v1";

// RC document type IDs (must match backend .env DOC_RC_DOCTYPE_FRONT / DOC_RC_DOCTYPE_BACK)
export const DOC_RC_DOCTYPE_FRONT = 1;
export const DOC_RC_DOCTYPE_BACK = 32;

const configuration = {
  BaseUrl,
  apiEndpoint,
  authApiEndpoint,
  GOOGLE_PLACES_API_KEY,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAP_KEY,
  MAP_AUTO_REFESH,
  referralEndpoint,
  DOC_RC_DOCTYPE_FRONT,
  DOC_RC_DOCTYPE_BACK,
};

export default configuration;
